import "./style.scss";
import { FC, useEffect, useMemo, useState } from "react";
import { defaultApi } from "@/src/api/axiosInstance";
import { IRowData } from "@/src/types/applicationOnline";
import { ApplicantDocumentFileType, documentsRequests } from "@/src/api/documentsRequests";
import { studentsRequests, StudentDocumentFileType } from "@/src/api/studentsRequests";
import { IApplicantDocument } from "@/src/types/applicant";
import {
  filterApplicantDocuments,
  getApplicantDocumentsFromUserInfo,
  selectorLabelToDocumentType,
} from "@/src/utils/applicants/applicantDocuments";
import TrashSvg from "@/src/svg/applicationsPage/APPopUp/Documents/TrashSvg";
import DocumentPreviewThumbnail from "@/src/components/CustomUtils/DocumentPreviewThumbnail/DocumentPreviewThumbnail";
import VerticalLine from "@/src/components/VerticalLine/VerticalLine";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import DocumentSvg from "@/src/svg/applicationsPage/APPopUp/Documents/DocumentSvg";
import SelectorArrowSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/SelectorArrowSvg";
import CustomSingleSelect from "@/src/components/CustomUtils/CustomSingleSelect/CustomSingleSelect";
import DotsSvg from "@/src/svg/applicationsPage/APPopUp/Documents/DotsSvg";
import SortSvg from "@/src/svg/applicationsPage/APPopUp/Comments/SortSvg";
import FormSummaryModal, { FileAttachmentField, FilesByFieldMap } from "@/src/components/ApplyOnline/FormSummaryModal/FormSummaryModal";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { buildSimpleHistoryText, createApplicantHistory } from "@/src/utils/applicants/createApplicantHistory";

const documentsFileFields: FileAttachmentField[] = [
  { name: "files_passport", title: "Passport" },
  { name: "files_certificate", title: "Certificate" },
  { name: "files_education", title: "Education" },
  { name: "files_another", title: "Another" },
];

const firstSelectorMass: string[] = ["All", "Passort", "Certificate", "Education", "Another"];
// const secondSelectorMass: string[] = ["University Second", "University Second", "University Second"];
// const thirdSelectorMass: string[] = ["University Third", "University Third", "University Third"];

type IUserInfo = IRowData["userInfo"];
type TSelectorValue = "All" | "Passort" | "Certificate" | "Education" | "Another";

interface IUserDocument {
  file_extension?: string;
  file_name?: string;
  file_path?: string;
  file_size?: number;
  created_at?: string;
}

type DocumentsSortOrder = "asc" | "desc";

const parseCreatedAt = (value?: string): number => {
  if (!value) return 0;
  const dmyHm = value.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[ ,](\d{2}):(\d{2})(?::\d{2})?)?$/);
  if (dmyHm) {
    const [, d, m, y, h = "00", min = "00"] = dmyHm;
    return Date.UTC(Number(y), Number(m) - 1, Number(d), Number(h), Number(min));
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortDocumentsByCreatedAt = (documents: IUserDocument[], order: DocumentsSortOrder): IUserDocument[] =>
  [...documents].sort((a, b) => {
    const diff = parseCreatedAt(a.created_at) - parseCreatedAt(b.created_at);
    return order === "asc" ? diff : -diff;
  });

interface IUserDocumentsBySelector {
  files_passport?: IUserDocument[];
  files_certificate?: IUserDocument[];
  files_education?: IUserDocument[];
  files_another?: IUserDocument[];
}

const tryParseObject = (value: unknown): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string") return null;

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch (_error) {
    return null;
  }

  return null;
};

const normalizeDocumentsBySelector = (value: unknown): IUserDocumentsBySelector => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as IUserDocumentsBySelector;
};

const mergeSelectors = (a: IUserDocumentsBySelector, b: IUserDocumentsBySelector): IUserDocumentsBySelector => ({
  files_passport: [...(a.files_passport ?? []), ...(b.files_passport ?? [])],
  files_certificate: [...(a.files_certificate ?? []), ...(b.files_certificate ?? [])],
  files_education: [...(a.files_education ?? []), ...(b.files_education ?? [])],
  files_another: [...(a.files_another ?? []), ...(b.files_another ?? [])],
});

/** One group can be an object with files_* keys or an array of such objects (merged). */
const normalizeGroupToSelector = (value: unknown): IUserDocumentsBySelector => {
  if (value == null) return {};
  if (Array.isArray(value)) {
    return value.reduce<IUserDocumentsBySelector>((acc, item) => mergeSelectors(acc, normalizeDocumentsBySelector(item)), {});
  }
  return normalizeDocumentsBySelector(value);
};

const getStudentsDocumentsGroup = (documents: Record<string, unknown>): unknown =>
  documents.studentsdocuments ?? documents.studentsDocuments ?? documents.students_documents ?? null;

const getUniversityDocumentsGroup = (documents: Record<string, unknown>): unknown =>
  documents.universitydocuments ?? documents.universityDocuments ?? documents.university_documents ?? null;

const toUserDocumentArray = (value: unknown): IUserDocument[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => item && typeof item === "object") as IUserDocument[];
};

/** Merge multiple object roots (e.g. documents is an array of { studentsDocuments, universityDocuments }). */
const mergeDocumentRoots = (roots: Record<string, unknown>[]): Record<string, unknown> => roots.reduce((acc, r) => ({ ...acc, ...r }), {});

const parseDocumentsRoot = (userInfo: IUserInfo | undefined): Record<string, unknown> => {
  if (!userInfo) return {};

  const raw = userInfo.documents;
  if (Array.isArray(raw)) {
    const merged = raw.map((item) => tryParseObject(item)).filter(Boolean) as Record<string, unknown>[];
    return mergeDocumentRoots(merged);
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const merged = parsed.map((item) => tryParseObject(item)).filter(Boolean) as Record<string, unknown>[];
        return mergeDocumentRoots(merged);
      }
    } catch {
      /* fall through */
    }
  }

  const fromDocumentsField = tryParseObject(raw);
  const fromUserInfo = tryParseObject(userInfo);
  const fromRootDocuments = tryParseObject(fromUserInfo?.documents);

  return fromDocumentsField ?? fromRootDocuments ?? fromUserInfo ?? {};
};

type DocumentsTab = "all" | "students" | "university";

const selectorToDocumentKey: Record<Exclude<TSelectorValue, "All">, keyof IUserDocumentsBySelector> = {
  Passort: "files_passport",
  Certificate: "files_certificate",
  Education: "files_education",
  Another: "files_another",
};

const fieldToApplicantFileType: Record<keyof IUserDocumentsBySelector, ApplicantDocumentFileType> = {
  files_passport: "passport",
  files_certificate: "certificate",
  files_education: "education",
  files_another: "another",
};

const fieldToStudentFileType: Record<keyof IUserDocumentsBySelector, StudentDocumentFileType> = fieldToApplicantFileType;

const resolveDocumentUrl = (path?: string): string => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const baseUrl = String(defaultApi.defaults.baseURL ?? "").replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

interface DocumentsProps {
  userInfo: IUserInfo | undefined;
  /** Applicant ID for /applicants and /documents upload endpoints. */
  studentId?: number;
  onRefreshUserInfo?: () => Promise<void>;
}

const Documents: FC<DocumentsProps> = ({ userInfo, studentId: applicantId, onRefreshUserInfo }) => {
  const { notify } = usePushNotifications();
  const [currentBody, setCurrentBody] = useState<DocumentsTab>("all");
  const [firstSelector, setFirstSelector] = useState<TSelectorValue>("All");
  const [openSelector, setOpenSelector] = useState<string | null>(null);
  const [isUploadModalActive, setIsUploadModalActive] = useState(false);
  const [pendingFilesMap, setPendingFilesMap] = useState<FilesByFieldMap>({});
  const [sortOrder, setSortOrder] = useState<DocumentsSortOrder>("asc");
  const canAddDocuments = currentBody !== "all";

  useEffect(() => {
    if (!canAddDocuments) {
      setIsUploadModalActive(false);
    }
  }, [canAddDocuments]);

  useEffect(() => {
    if (currentBody === "university") {
      setFirstSelector("All");
      setOpenSelector(null);
    }
  }, [currentBody]);
  // const [secondSelector, setSecondSelector] = useState<string>("");
  // const [thirdSelector, setThirdSelector] = useState<string>("");

  const applicantDocuments = useMemo(() => getApplicantDocumentsFromUserInfo(userInfo), [userInfo]);
  const usesApplicantDocuments = applicantDocuments !== null;

  const currentSelector: IUserDocumentsBySelector = useMemo(() => {
    if (usesApplicantDocuments) return {};

    const documents = parseDocumentsRoot(userInfo);
    const studentsGroup = normalizeGroupToSelector(getStudentsDocumentsGroup(documents));
    const universityRaw = getUniversityDocumentsGroup(documents);
    const universityDocsArray = toUserDocumentArray(universityRaw);
    const universityGroup =
      universityDocsArray.length > 0 ? { files_another: universityDocsArray } : normalizeGroupToSelector(universityRaw);

    if (currentBody === "all") return mergeSelectors(studentsGroup, universityGroup);
    if (currentBody === "students") return studentsGroup;
    return universityGroup;
  }, [userInfo, currentBody, usesApplicantDocuments]);

  const handleStudentsUpload = async (filesMap: FilesByFieldMap) => {
    if (!applicantId) {
      notify({
        title: "Upload failed",
        message: "Applicant ID is missing.",
        type: "error",
      });
      return;
    }

    const filesToUpload: { file: File; fileType: ApplicantDocumentFileType }[] = [];
    for (const field of Object.keys(fieldToApplicantFileType) as (keyof IUserDocumentsBySelector)[]) {
      const fileType = fieldToApplicantFileType[field];
      for (const file of filesMap[field] ?? []) {
        filesToUpload.push({ file, fileType });
      }
    }

    if (!filesToUpload.length) {
      notify({
        title: "Nothing to upload",
        message: "Please attach at least one file.",
        type: "error",
      });
      return;
    }

    let uploadedCount = 0;
    for (const { file, fileType } of filesToUpload) {
      const uploadResult = usesApplicantDocuments
        ? await documentsRequests.uploadApplicantDocument(applicantId, fileType, file)
        : await studentsRequests.uploadStudentDocument(applicantId, fileType, file);
      if (!uploadResult) continue;
      uploadedCount += 1;
    }

    if (uploadedCount === 0) {
      notify({
        title: "Upload failed",
        message: "Could not upload student documents.",
        type: "error",
      });
      return;
    }

    await onRefreshUserInfo?.();
    setPendingFilesMap({});
    await createApplicantHistory(
      applicantId,
      buildSimpleHistoryText(
        "documents",
        `Uploaded ${uploadedCount} student document${uploadedCount === 1 ? "" : "s"}`,
      ),
    );
    notify({
      title: "Documents uploaded",
      message:
        uploadedCount === filesToUpload.length
          ? "Student documents were added successfully."
          : `Uploaded ${uploadedCount} of ${filesToUpload.length} files.`,
      type: "success",
    });
  };

  const handleUniversityUpload = async (filesMap: FilesByFieldMap) => {
    if (!applicantId) {
      notify({
        title: "Upload failed",
        message: "Applicant ID is missing.",
        type: "error",
      });
      return;
    }

    const filesToUpload = [
      ...(filesMap.files_passport ?? []),
      ...(filesMap.files_certificate ?? []),
      ...(filesMap.files_education ?? []),
      ...(filesMap.files_another ?? []),
    ];

    if (!filesToUpload.length) {
      notify({
        title: "Nothing to upload",
        message: "Please attach at least one file.",
        type: "error",
      });
      return;
    }

    let uploadedCount = 0;
    for (const file of filesToUpload) {
      const uploadResult = usesApplicantDocuments
        ? await documentsRequests.uploadUniversityDocument(applicantId, file)
        : await studentsRequests.uploadUniversityDocuments(applicantId, file);
      if (!uploadResult) continue;
      uploadedCount += 1;
    }

    if (uploadedCount === 0) {
      notify({
        title: "Upload failed",
        message: "Could not upload university documents.",
        type: "error",
      });
      return;
    }

    await onRefreshUserInfo?.();
    setPendingFilesMap({});
    await createApplicantHistory(
      applicantId,
      buildSimpleHistoryText(
        "documents",
        `Uploaded ${uploadedCount} university document${uploadedCount === 1 ? "" : "s"}`,
      ),
    );
    notify({
      title: "Documents uploaded",
      message:
        uploadedCount === filesToUpload.length
          ? "University documents were added successfully."
          : `Uploaded ${uploadedCount} of ${filesToUpload.length} files.`,
      type: "success",
    });
  };

  const filteredApplicantDocuments: IApplicantDocument[] = useMemo(() => {
    if (!usesApplicantDocuments || applicantDocuments === null) return [];
    return filterApplicantDocuments(applicantDocuments, currentBody, selectorLabelToDocumentType(firstSelector));
  }, [usesApplicantDocuments, applicantDocuments, currentBody, firstSelector]);

  const filteredDocuments: IUserDocument[] = useMemo(() => {
    if (usesApplicantDocuments) {
      return filteredApplicantDocuments.map((doc) => ({
        file_extension: doc.file_extension,
        file_name: doc.file_name,
        file_path: doc.file_path,
        file_size: doc.file_size,
        created_at: doc.created_at,
      }));
    }

    if (firstSelector === "All") {
      return [
        ...(currentSelector.files_passport ?? []),
        ...(currentSelector.files_certificate ?? []),
        ...(currentSelector.files_education ?? []),
        ...(currentSelector.files_another ?? []),
      ];
    }

    const targetKey = selectorToDocumentKey[firstSelector];
    return currentSelector[targetKey] ?? [];
  }, [usesApplicantDocuments, filteredApplicantDocuments, currentSelector, firstSelector]);

  const sortedDocuments = useMemo(
    () => sortDocumentsByCreatedAt(filteredDocuments, sortOrder),
    [filteredDocuments, sortOrder],
  );

  const hasAnyDocuments = useMemo(() => {
    if (usesApplicantDocuments) {
      return filterApplicantDocuments(applicantDocuments ?? [], currentBody, "all").length > 0;
    }

    return (
      (currentSelector.files_passport?.length ?? 0) +
        (currentSelector.files_certificate?.length ?? 0) +
        (currentSelector.files_education?.length ?? 0) +
        (currentSelector.files_another?.length ?? 0) >
      0
    );
  }, [usesApplicantDocuments, applicantDocuments, currentBody, currentSelector]);

  const documentsListKey = useMemo(
    () =>
      sortedDocuments
        .map((item) => `${item.file_path ?? ""}:${item.file_name ?? ""}:${item.created_at ?? ""}`)
        .join("|"),
    [sortedDocuments, sortOrder],
  );

  // const handleSecondSelector = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //   setSecondSelector(event.target.value);
  // };
  // const handleThirdSelector = (event: React.ChangeEvent<HTMLSelectElement>) => {
  //   setThirdSelector(event.target.value);
  // };

  return (
    <div className="documents">
      <div className="documents-header">
        <div className="documents-header-tabs">
          <div className="documents-header-tab" onClick={() => setCurrentBody("all")}>
            <BtnDefault reverse={currentBody === "all" && true}>
              <p>All</p>
            </BtnDefault>
          </div>
          <div className="documents-header-tab" onClick={() => setCurrentBody("students")}>
            <BtnDefault reverse={currentBody === "students" && true}>
              <p>Students</p>
            </BtnDefault>
          </div>
          <div className="documents-header-tab" onClick={() => setCurrentBody("university")}>
            <BtnDefault reverse={currentBody === "university" && true}>
              <p>University</p>
            </BtnDefault>
          </div>
        </div>
        {canAddDocuments && (
          <div className="documents-header-new">
            <BtnDefault onClick={() => setIsUploadModalActive(true)}>
              <p>+</p>
            </BtnDefault>
          </div>
        )}
        <div className="documents-header-content">
          {currentBody !== "university" && (
            <div className="documents-header-content-selectors">
              <div className="documents-header-content-selector">
                <input
                  readOnly
                  value={firstSelector}
                  onClick={() => setOpenSelector((prev) => (prev === "firstSelector" ? null : "firstSelector"))}
                />
                <SelectorArrowSvg />
                {openSelector === "firstSelector" && (
                  <CustomSingleSelect
                    data={firstSelectorMass}
                    isSearch={false}
                    allowAdd={false}
                    columnValue={firstSelector}
                    onChange={(label) => setFirstSelector(label as TSelectorValue)}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            </div>
          )}
          <BtnDefault onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}>
            <p>Sort</p>
            <SortSvg />
          </BtnDefault>
          <div className="documents-header-content-line">
            <VerticalLine />
          </div>
          <div className="documents-header-content-delete">
            <TrashSvg />
          </div>
        </div>
      </div>
      <div className="documents-body">
        <div
          key={documentsListKey}
          className={`documents-body-items ${!hasAnyDocuments ? "no-documents" : ""}`}
        >
          {!hasAnyDocuments && <p>The user currently has no documents.</p>}
          {hasAnyDocuments &&
            sortedDocuments.map((item, index) => {
              const fileUrl = resolveDocumentUrl(item.file_path);

              return (
                <div className="documents-body-items-item" key={`${item.file_path ?? item.file_name ?? "doc"}-${index}`}>
                  <div className="documents-body-items-item-header">
                    <div className="documents-body-items-item-header-document">
                      <DocumentSvg />
                    </div>
                    <div className="documents-body-items-item-header-title">
                      <p>{item.file_name}</p>
                    </div>
                    <div className="documents-body-items-item-header-dots">
                      <DotsSvg />
                    </div>
                  </div>
                  <a href={fileUrl || "#"} rel="noreferrer" target="_blank" className="documents-body-items-item-preview">
                    <DocumentPreviewThumbnail
                      fileUrl={fileUrl}
                      fileExtension={item.file_extension}
                      fileName={item.file_name}
                      filePath={item.file_path}
                    />
                  </a>
                </div>
              );
            })}
        </div>
      </div>
      {canAddDocuments && (
        <FormSummaryModal
          isActive={isUploadModalActive}
          onClose={() => setIsUploadModalActive(false)}
          fields={documentsFileFields}
          selectedFilesMap={pendingFilesMap}
          title="Add documents"
          subtitle="Attach files to upload"
          submitLabel="Upload files →"
          onAttach={async (filesMap) => {
            setPendingFilesMap(filesMap);
            if (currentBody === "students") {
              await handleStudentsUpload(filesMap);
            } else if (currentBody === "university") {
              await handleUniversityUpload(filesMap);
            }
            setIsUploadModalActive(false);
          }}
        />
      )}
    </div>
  );
};

export default Documents;
